import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import { ArrowLeft, X } from 'lucide-react';
import AssessmentResults from './AssessmentResults';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const COMPLETE_BUYER_ASSESSMENT = gql`
  mutation CompleteBuyerAssessment(
    $responses: [AssessmentResponseInput!]!
    $assessmentType: String!
  ) {
    completeBuyerAssessment(
      responses: $responses
      assessmentType: $assessmentType
    ) {
      buyer_assessment_result {
        id
        totalScore
        sectionScores
        readinessBand
        bandLabel
        completedAt
      }
      errors
    }
  }
`;

// ---------------------------------------------------------------------------
// Likert scale labels
// ---------------------------------------------------------------------------

const LIKERT_LABELS = {
  1: 'Strongly Disagree',
  2: 'Disagree',
  3: 'Neutral',
  4: 'Agree',
  5: 'Strongly Agree',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function capitalize(str) {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Thin progress bar fixed to the top of the assessment panel. */
function ProgressBar({ current, total }) {
  const pct = total > 0 ? ((current + 1) / total) * 100 : 0;
  return (
    <div className="w-full h-1 bg-succession-bg-tertiary rounded-full overflow-hidden">
      <div
        className="h-full bg-succession-primary rounded-full transition-all duration-500 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Section interstitial — shown when the category changes. */
function SectionTransition({ categoryLabel, onContinue }) {
  return (
    <div
      className="flex flex-col items-center justify-center py-20 text-center"
      style={{ animation: 'fadeIn 0.35s ease-out' }}
    >
      <div className="w-12 h-12 rounded-full bg-succession-primary/10 flex items-center justify-center mb-6">
        <span className="text-succession-primary text-xl font-semibold">&rarr;</span>
      </div>
      <h2 className="text-2xl font-light text-succession-text-primary mb-2">
        Next Section
      </h2>
      <p className="text-succession-text-secondary text-sm mb-8">
        {categoryLabel}
      </p>
      <button
        onClick={onContinue}
        className="bg-succession-primary text-white rounded-full px-8 py-3 font-medium text-sm hover:bg-succession-primary-hover transition-colors"
      >
        Continue &rarr;
      </button>
    </div>
  );
}

/** Likert scale (1-5) answer buttons. */
function LikertScale({ value, onChange, scaleMax }) {
  const count = scaleMax || 5;
  const labels = count === 5 ? LIKERT_LABELS : null;

  return (
    <div className="space-y-3">
      {Array.from({ length: count }, (_, i) => i + 1).map(n => {
        const isSelected = value === n;
        return (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border text-left transition-all duration-200 ${
              isSelected
                ? 'bg-succession-primary text-white border-succession-primary shadow-succession'
                : 'bg-succession-bg-primary text-succession-text-primary border-succession-border hover:border-succession-primary hover:bg-succession-bg-secondary'
            }`}
          >
            <span
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 transition-colors duration-200 ${
                isSelected
                  ? 'bg-white/20 text-white'
                  : 'bg-succession-bg-tertiary text-succession-text-secondary'
              }`}
            >
              {n}
            </span>
            {labels && labels[n] && (
              <span className="text-sm font-medium">{labels[n]}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Yes / No answer buttons. */
function YesNoScale({ value, onChange }) {
  return (
    <div className="flex gap-4">
      {[
        { label: 'Yes', val: 2 },
        { label: 'No', val: 1 },
      ].map(opt => {
        const isSelected = value === opt.val;
        return (
          <button
            key={opt.label}
            onClick={() => onChange(opt.val)}
            className={`flex-1 py-6 rounded-xl border text-center font-medium text-lg transition-all duration-200 ${
              isSelected
                ? 'bg-succession-primary text-white border-succession-primary shadow-succession'
                : 'bg-succession-bg-primary text-succession-text-primary border-succession-border hover:border-succession-primary hover:bg-succession-bg-secondary'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function BuyerReadinessAssessment({
  questions,
  assessmentType,
  loading: questionsLoading,
  error: questionsError,
}) {
  // Phase: 'questions' | 'section_transition' | 'submitting' | 'results'
  const [phase, setPhase] = useState('questions');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const [completeBuyerAssessment] = useMutation(COMPLETE_BUYER_ASSESSMENT);

  const totalQuestions = questions?.length || 0;
  const currentQuestion = questions?.[currentIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;

  // Detect category boundaries for section transitions
  const currentCategory = currentQuestion?.category;
  const prevQuestion = currentIndex > 0 ? questions[currentIndex - 1] : null;
  const prevCategory = prevQuestion?.category;
  const isNewSection = currentIndex > 0 && currentCategory && prevCategory && currentCategory !== prevCategory;

  // Determine question type: Likert (scale_max >= 2) vs Yes/No (scale_max <= 1)
  const isYesNo = currentQuestion && (currentQuestion.scale_max <= 1);
  const isLikert = !isYesNo;

  // Show section transition when entering a new category
  useEffect(() => {
    if (isNewSection && phase === 'questions') {
      setPhase('section_transition');
    }
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleAnswer = useCallback(
    (value) => {
      if (!currentQuestion) return;
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    },
    [currentQuestion]
  );

  const handleAnswerAndAdvance = useCallback(
    (value) => {
      if (!currentQuestion) return;
      const newAnswers = { ...answers, [currentQuestion.id]: value };
      setAnswers(newAnswers);

      const isLast = currentIndex === totalQuestions - 1;
      if (isLast) {
        // Submit automatically
        handleSubmit(newAnswers);
      } else {
        // Check if next question is a new section
        const nextQ = questions[currentIndex + 1];
        if (nextQ && nextQ.category !== currentQuestion.category) {
          setCurrentIndex(prev => prev + 1);
          setPhase('section_transition');
        } else {
          setCurrentIndex(prev => prev + 1);
        }
      }
    },
    [currentQuestion, currentIndex, totalQuestions, questions, answers] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setPhase('questions');
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const handleContinueFromTransition = useCallback(() => {
    setPhase('questions');
  }, []);

  const handleSubmit = useCallback(
    async (finalAnswers) => {
      const answersToSubmit = finalAnswers || answers;
      setPhase('submitting');
      setSubmitError(null);

      try {
        const responses = Object.entries(answersToSubmit).map(([questionId, value]) => ({
          questionId,
          value,
        }));

        const { data } = await completeBuyerAssessment({
          variables: {
            responses,
            assessmentType: assessmentType || 'buyer_full',
          },
        });

        const payload = data?.completeBuyerAssessment;
        if (payload?.errors?.length) {
          setSubmitError(payload.errors.join(', '));
        } else if (payload?.buyer_assessment_result) {
          setResult(payload.buyer_assessment_result);
          setPhase('results');
        } else {
          setSubmitError('No result returned. Please try again.');
          setPhase('questions');
        }
      } catch (err) {
        console.error('Failed to submit buyer assessment:', err);
        setSubmitError(err.message || 'An error occurred. Please try again.');
        setPhase('questions');
      }
    },
    [answers, assessmentType, completeBuyerAssessment]
  );

  const handleRetake = useCallback(() => {
    setAnswers({});
    setCurrentIndex(0);
    setResult(null);
    setSubmitError(null);
    setPhase('questions');
  }, []);

  const handleClose = useCallback(() => {
    if (Object.keys(answers).length > 0) {
      if (!window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
        return;
      }
    }
    window.location.href = '/succession/home';
  }, [answers]);

  // ---------------------------------------------------------------------------
  // Loading / Error states
  // ---------------------------------------------------------------------------

  if (questionsLoading) {
    return (
      <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center p-6">
        <div className="bg-succession-bg-primary rounded-2xl p-10 max-w-xl w-full shadow-succession-lg text-center border border-succession-border">
          <div className="inline-block w-8 h-8 border-2 border-succession-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm text-succession-text-secondary">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (questionsError || !questions || questions.length === 0) {
    return (
      <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center p-6">
        <div className="bg-succession-bg-primary rounded-2xl p-10 max-w-xl w-full shadow-succession-lg text-center border border-succession-border">
          <p className="text-sm text-succession-text-secondary">
            {questionsError
              ? 'Failed to load assessment questions. Please try again later.'
              : 'No assessment questions are available at this time.'}
          </p>
          <button
            onClick={() => (window.location.href = '/succession/home')}
            className="mt-4 text-sm text-succession-primary hover:underline"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Results phase
  // ---------------------------------------------------------------------------

  if (phase === 'results' && result) {
    return (
      <AssessmentResults
        totalScore={result.totalScore}
        sectionScores={result.sectionScores}
        readinessBand={result.readinessBand}
        bandLabel={result.bandLabel}
        assessmentType={assessmentType}
        onRetake={handleRetake}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Submitting phase
  // ---------------------------------------------------------------------------

  if (phase === 'submitting') {
    return (
      <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center p-6">
        <div className="bg-succession-bg-primary rounded-2xl p-10 max-w-xl w-full shadow-succession-lg text-center border border-succession-border">
          <div className="inline-block w-8 h-8 border-2 border-succession-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm text-succession-text-secondary">Calculating your results...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Question / Transition phase
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center p-4 md:p-8">
      <div className="w-full max-w-xl relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute -top-2 -right-2 md:top-0 md:right-0 z-10 w-8 h-8 flex items-center justify-center rounded-full text-succession-text-tertiary hover:text-succession-text-primary hover:bg-succession-bg-tertiary transition-colors"
          title="Exit assessment"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Card */}
        <div className="bg-succession-bg-primary rounded-2xl border border-succession-border shadow-succession-lg overflow-hidden">
          {/* Progress bar */}
          <div className="px-8 pt-6">
            <ProgressBar current={currentIndex} total={totalQuestions} />
            <div className="flex justify-between items-center mt-2 mb-6">
              <span className="text-xs font-mono uppercase tracking-wide text-succession-text-tertiary">
                Question {currentIndex + 1} of {totalQuestions}
              </span>
              {currentCategory && (
                <span className="text-xs font-medium text-succession-primary px-2 py-0.5 rounded-full bg-succession-primary-light">
                  {capitalize(currentCategory)}
                </span>
              )}
            </div>
          </div>

          {/* Content area */}
          <div className="px-8 pb-8">
            {phase === 'section_transition' ? (
              <SectionTransition
                categoryLabel={capitalize(currentCategory)}
                onContinue={handleContinueFromTransition}
              />
            ) : (
              <div
                key={currentIndex}
                style={{ animation: 'fadeIn 0.3s ease-out' }}
              >
                {/* Question text */}
                <h2 className="text-xl md:text-2xl font-medium text-succession-text-primary leading-relaxed mb-8">
                  {currentQuestion?.text}
                </h2>

                {/* Error banner */}
                {submitError && (
                  <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                {/* Answer controls */}
                {isYesNo ? (
                  <YesNoScale
                    value={currentAnswer}
                    onChange={handleAnswerAndAdvance}
                  />
                ) : (
                  <LikertScale
                    value={currentAnswer}
                    onChange={handleAnswerAndAdvance}
                    scaleMax={currentQuestion?.scale_max}
                  />
                )}

                {/* Navigation */}
                <div className="flex justify-between items-center mt-10">
                  <button
                    onClick={handleBack}
                    disabled={currentIndex === 0}
                    className="inline-flex items-center gap-1.5 text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>

                  {/* Keyboard hint */}
                  <span className="text-xs text-succession-text-tertiary hidden md:block">
                    Select an option to continue
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
